import React from "react";
import { Link } from "react-router-dom";
import { withImageVersion } from "../lib/imageVersion";

/**
 * AngelCard — single tile in the grid.
 * Props:
 *  - angel: angel object from /api/angels
 *  - selectedNumber: the user's currently-saved angel number (or null)
 *  - index: zero-based index for stagger animation
 *  - recentlyChosen: another member picked this Angel within the last hour →
 *    emit a soft brass heartbeat pulse so the grid feels live.
 *  - showWaitingNote: viewer has no Angel yet → gently spotlight angels
 *    still waiting for their first member.
 */
export default function AngelCard({ angel, selectedNumber, index = 0, recentlyChosen = false, showWaitingNote = false }) {
    const isSelected = selectedNumber && selectedNumber === angel.number;
    const isUnavailable = !angel.isSelectable;
    // "New design" ribbon — newest design added within the last 7 days.
    // Gated on >1 designs so the migration fold (Design I) never triggers it.
    const newestDesign = angel.designs?.length > 1 ? angel.designs[angel.designs.length - 1] : null;
    const hasNewDesign = Boolean(
        newestDesign && Date.now() - new Date(newestDesign.uploadedAt).getTime() < 7 * 24 * 3600 * 1000
    );
    // Freshly-landed angel (opened <14d ago) with no approved attributes yet.
    const isNewArrival = Boolean(
        angel.openedForSelectionAt
        && Date.now() - new Date(angel.openedForSelectionAt).getTime() < 14 * 24 * 3600 * 1000
    );

    const base =
        "relative flex flex-col group overflow-hidden bg-[#151311] border transition-all duration-500 rounded-sm card-enter";
    const stateClass = isSelected
        ? "border-[#cfa052] shadow-[0_0_28px_rgba(207,160,82,0.18)] ring-1 ring-[#cfa052]/50"
        : isUnavailable
            ? "border-white/5 opacity-70 cursor-not-allowed"
            : "border-white/5 hover:border-[#cfa052]/40 hover:-translate-y-1 hover:shadow-[0_10px_32px_rgba(207,160,82,0.06)]";

    const ImageBlock = (
        <div className="aspect-[5/4] w-full overflow-hidden relative bg-[#0c0b0a]">
            <img
                src={withImageVersion(angel.mainImageUrl, angel.imageVersion)}
                alt={`Angel ${angel.number}`}
                loading="lazy"
                className={`w-full h-full object-cover transition-transform duration-700 ease-out ${
                    isUnavailable ? "grayscale opacity-40" : "group-hover:scale-105"
                }`}
            />
            {/* hover wash — soft brass glow that ripples from the centre out
                when the card is hovered/tapped. Pure decoration; pointer-events
                disabled. Hidden for unavailable angels. */}
            {!isUnavailable && (
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-[opacity,transform] duration-700 ease-out"
                    style={{
                        background:
                            "radial-gradient(ellipse at center, rgba(207,160,82,0.30) 0%, rgba(207,160,82,0.10) 45%, transparent 78%)",
                    }}
                />
            )}
            {/* recently-chosen heartbeat — one quiet brass pulse every ~15s
                when another member picked this Angel within the last hour.
                Provides a live "the squad is moving" signal without polling
                feeling spammy. Skipped on unavailable cards (no picks possible)
                and on the user's own selected angel (would be a vanity loop). */}
            {recentlyChosen && !isUnavailable && !isSelected && (
                <div
                    aria-hidden="true"
                    data-testid={`angel-card-pulse-${angel.number}`}
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at center, rgba(255,210,140,0.32) 0%, rgba(207,160,82,0.14) 35%, transparent 72%)",
                        animation: "as-card-pulse 15s ease-out infinite",
                        opacity: 0,
                    }}
                />
            )}
            {/* corner darkener — fades the four corners of the artwork toward
                the card background so angels painted on coloured BGs (e.g. 049
                purple, 051 green) blend like their neighbours. Transparent in
                the middle so well-composed centre-pieces are untouched. */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: "radial-gradient(ellipse at center, transparent 58%, rgba(12,11,10,0.55) 82%, #0c0b0a 100%)",
                }}
            />
            {/* bottom vignette — darkens the lower portion for the number stamp */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0c0b0a]/90 via-[#0c0b0a]/20 to-transparent" />
            {/* numeral overlay — bold for placeholders, subtle stamp for real artwork */}
            {angel.hasArtwork ? (
                <div className="absolute bottom-3 left-4 pointer-events-none">
                    <span className="font-numerals text-2xl text-[#cfa052] drop-shadow-[0_1px_8px_rgba(0,0,0,0.9)]">
                        {angel.number}
                    </span>
                </div>
            ) : (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span
                        className={`font-numerals text-7xl md:text-8xl ${
                            isUnavailable ? "text-[#e8e4db]/30" : "text-[#e8e4db]/85"
                        } drop-shadow-[0_2px_24px_rgba(0,0,0,0.6)]`}
                    >
                        {angel.number}
                    </span>
                </div>
            )}
            {hasNewDesign ? (
                <div
                    className="absolute top-3 left-3 bg-[#cfa052] text-[#0c0b0a] text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-sm z-10 shadow-[0_0_16px_rgba(207,160,82,0.5)]"
                    data-testid={`angel-card-new-design-${angel.number}`}
                >
                    New design
                </div>
            ) : angel.designs?.length > 1 && (
                <div
                    className="absolute top-3 left-3 bg-[#0c0b0a]/70 backdrop-blur-sm border border-[#cfa052]/30 text-[#cfa052] text-[9px] font-label uppercase tracking-[0.2em] px-2 py-1 rounded-sm z-10"
                    data-testid={`angel-card-designs-${angel.number}`}
                >
                    {angel.designs.length} designs
                </div>
            )}
            {isSelected && (
                <div className="absolute top-3 right-3 bg-[#cfa052] text-[#0c0b0a] text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-sm z-10" data-testid={`angel-selected-badge-${angel.number}`}>
                    Chosen
                </div>
            )}
        </div>
    );

    const FooterBlock = (
        <div className="px-4 md:px-5 py-4 border-t border-white/5">
            <div className="flex items-baseline justify-between gap-3">
                <span className="font-label text-[10px] text-[#cfa052]">№ {angel.number}</span>
                <span className={`text-[11px] ${isUnavailable ? "font-poetic text-[#6e6a62]" : "font-label text-[#9a958a]"}`}>
                    {isUnavailable ? angel.statusText : "View"}
                </span>
            </div>
            {!isUnavailable && (
                <p
                    className={`mt-2 font-poetic text-[12px] leading-snug truncate ${
                        angel.approvedAttributes?.length ? "text-[#9a958a]" : "text-[#6e6a62]"
                    }`}
                    data-testid={`angel-card-status-${angel.number}`}
                >
                    {angel.approvedAttributes?.length
                        ? angel.approvedAttributes.join(" · ")
                        : isNewArrival
                            ? "Newly arrived — be the first to suggest their attributes"
                            : angel.statusText}
                </p>
            )}
            {showWaitingNote && angel.waiting && !isUnavailable && !isSelected && (
                <p
                    className="mt-1.5 font-poetic italic text-[11px] leading-snug text-[#cfa052]/70"
                    data-testid={`angel-card-waiting-${angel.number}`}
                >
                    Still waiting for someone.
                </p>
            )}
        </div>
    );

    const styleVar = { animationDelay: `${Math.min(index, 30) * 30}ms` };

    if (isUnavailable) {
        return (
            <div
                className={`${base} ${stateClass}`}
                style={styleVar}
                data-testid={`angel-card-${angel.number}`}
                data-state="unavailable"
            >
                {ImageBlock}
                {FooterBlock}
            </div>
        );
    }

    return (
        <Link
            to={`/angels/${angel.number}`}
            className={`${base} ${stateClass} cursor-pointer`}
            style={styleVar}
            data-testid={`angel-card-${angel.number}`}
            data-state={isSelected ? "selected" : "selectable"}
        >
            {ImageBlock}
            {FooterBlock}
        </Link>
    );
}
